import { cva, type VariantProps } from 'class-variance-authority'
import { AlertTriangle } from 'lucide-react'

import { cn } from '@/customer/lib/utils'

// --- FormDescription ---

const formDescriptionVariants = cva('text-muted-foreground', {
  variants: {
    size: {
      default: 'text-xs',
      lg: 'text-sm',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

interface FormDescriptionProps
  extends React.ComponentProps<'p'>,
    VariantProps<typeof formDescriptionVariants> {}

export function FormDescription({ className, size, ...props }: FormDescriptionProps) {
  return <p className={cn(formDescriptionVariants({ size }), className)} {...props} />
}

// --- FormError ---

type ErrorValue = string | { message: string } | undefined

interface FormErrorProps extends React.ComponentProps<'p'> {
  errors?: ErrorValue[]
}

function formatError(error: ErrorValue): string | null {
  if (!error) return null
  if (typeof error === 'string') return error
  return error.message
}

export function FormError({ errors, className, ...props }: FormErrorProps) {
  const firstError = errors?.find((e) => e != null)
  const message = formatError(firstError)

  if (!message) return null

  return (
    <p
      className={cn(
        'text-label-md font-medium text-destructive inline-flex items-center',
        className
      )}
      {...props}
    >
      <AlertTriangle className="size-3.5 mr-1" aria-hidden />
      {message}
    </p>
  )
}

// --- FormLabel ---

const formLabelVariants = cva('flex items-center gap-2 font-bold select-none', {
  variants: {
    size: {
      default: 'text-xs',
      lg: 'text-sm',
    },
    variant: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

export type FormLabelVariants = VariantProps<typeof formLabelVariants>

interface FormLabelProps extends React.ComponentProps<'label'>, FormLabelVariants {
  disabled?: boolean
  error?: boolean
  /**
   * Whether the field is required. Defaults to `true`. Optional fields render
   * a muted `(optional)` suffix; required fields have no marker.
   */
  required?: boolean
}

export function FormLabel({
  className,
  variant,
  size,
  disabled,
  error,
  children,
  htmlFor,
  required = true,
  ...props
}: FormLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        formLabelVariants({ variant, size }),
        disabled && 'text-muted-foreground cursor-not-allowed',
        error && 'text-destructive',
        className
      )}
      {...props}
    >
      {children}
      {!required && <span className="font-normal text-muted-foreground">(optional)</span>}
    </label>
  )
}
